import * as React from 'react';
import moment from 'moment';
import { withRouter } from 'react-router';
import useMountEffect from '../../hooks/useMountEffect';
import { connect } from 'react-redux';
import { Button, Col, Input, Row, Select, Spin } from 'antd';
import { PlusOutlined, FileDoneOutlined } from '@ant-design/icons';
import { Sorter } from '../WorkOrderTable/sorters/Sorters';
import { debounce } from '../../utils/PerformanceUtils';
import {
	DELIVERABLE_STATUS_DISPLAY_NAMES,
	DELIVERABLE_STATUS_OPTIONS,
} from '../../constants/DeliverableStatuses';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import DeliverableListItem from '../deliverable_list_item/DeliverableListItem';
import DeliverableDetailPane from '../deliverable_detail_pane/DeliverableDetailPane';
import DeliverableFormModal from '../deliverable_form_modal/DeliverableFormModal';
import { withUserType } from '../withUserType';
import { EmptyState } from '../empty_state/EmptyState';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	DELIVERABLES_GRAPH_TC,
	DELIVERABLES_INDEX_TC,
	getAllDependents,
	getDependsOnDeliverable,
} from '../../utils/DeliverableUtils';
import { runWithMessageFeedback } from '../../utils/MessageFeedbackUtils';
import { DELIVERABLES_CRUD_ACTION_CREATORS } from '../../actions/deliverables_actions';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';

const TC_NAME = DELIVERABLES_INDEX_TC;
const MESSAGE_KEY = 'deliverable-op';
// Page size for the dependency-graph fetch. 50 matches the list's own page size and
// the backend's warn-above-50 threshold (PaginationExtension).
const GRAPH_PAGE_SIZE = 50;

// Deliverables list sort options (shared Sorter item shape: label / selectVal /
// value:{sort_by, order}). dueDate + createdAt are the only backend-supported sorts
// (ProjectDeliverableDao). `order` is antd-style — the read thunk maps 'ascend'→'asc',
// anything else→'desc'. Missing due dates sort last on the backend (nullsLast).
const DELIVERABLE_SORTERS = [
	{ label: 'Earliest due', selectVal: 'dueDate', value: { sort_by: 'dueDate', order: 'ascend' } },
	{ label: 'Newest', selectVal: 'createdAt', value: { sort_by: 'createdAt', order: 'desc' } },
];
const DEFAULT_SORTING = DELIVERABLE_SORTERS[0].value;

const StaticOptionsSelect = ({
	value,
	onChange,
	items,
	mode,
	placeholder,
	allowClear = true,
	style,
}) => (
	<Select
		mode={mode}
		value={value}
		onChange={onChange}
		allowClear={allowClear}
		placeholder={placeholder}
		style={style || { width: '100%' }}
		options={items}
	/>
);

const DeliverablesTabPage = ({
	match,
	history,
	userType,
	currentUser,
	deliverables,
	allDeliverables,
	detail,
	fetching,
	filters,
	deliverablesSlice,
	fetchDeliverables,
	fetchAllDeliverables,
	getDeliverable,
	deleteDeliverable,
	updateDeliverable,
	updateFilters,
	clearAndUpdateFilters,
}) => {
	const projectId = match.params.id;
	const selectedId = match.params.deliverableId;
	const currency = getCurrency({ currentUser });

	const [modalVisible, setModalVisible] = React.useState(false);
	// Track only the id being edited (null = create). The modal reads the live
	// record from Redux by this id, so link/unlink from inside the modal is never lost.
	const [editingId, setEditingId] = React.useState(null);
	// Own search input (not AdvancedFilters') — AdvancedFilters swallows a clear back
	// to the initial value, so clearing it never refetched. This drives the fetch on
	// every change, including clear-to-empty. Same approach as the WO table's search.
	const [searchText, setSearchText] = React.useState('');
	const [sorting, setSorting] = React.useState(DEFAULT_SORTING);

	const deliverablesUrl = `/${userType}/projects/detail/${projectId}/deliverables`;

	const fetchWith = (nextFilters, nextSorting = sorting) =>
		fetchDeliverables(
			{
				projectId,
				search: nullSafeGet('search', nextFilters),
				status: nullSafeGet('status', nextFilters),
			},
			nextSorting
		);
	// Any change to the list can change the dependency graph (a new deliverable, a
	// deleted one, a moved date), so refresh both. Filter/search changes call
	// fetchWith directly — they only narrow the view, the graph is unaffected.
	const loadList = () => {
		fetchAllDeliverables(projectId);
		return fetchWith(filters);
	};

	// Sorter's onChange hands back the item's { sort_by, order } value directly.
	const onSortChange = (nextSorting) => {
		setSorting(nextSorting);
		fetchWith(filters, nextSorting);
	};

	const selectDeliverable = (id, replace = false) => {
		const url = `${deliverablesUrl}/${id}`;
		if (replace) history.replace(url);
		else history.push(url);
		getDeliverable(id);
	};

	// Select the first deliverable when nothing is selected; clear the selection
	// when the list is empty (e.g. after deleting the last one).
	const selectFirstOrClear = (list) => {
		if (list && list.length) selectDeliverable(list[0].id, true);
		else history.replace(deliverablesUrl);
	};

	useMountEffect(() => {
		// Always land on a fresh, unfiltered list — reset any filters persisted in the
		// slice from a previous visit so revisiting Deliverables doesn't inherit them.
		clearAndUpdateFilters({}, TC_NAME);
		if (selectedId) getDeliverable(selectedId);
		fetchAllDeliverables(projectId);
		fetchWith({}).then((list) => {
			if (!selectedId) selectFirstOrClear(list);
		});
	});

	// Wrap the AdvancedFilters callbacks so a filter change both updates the store
	// slice (keeps the filter UI in sync) and re-fetches the list server-side.
	const applyUpdateFilters = (patch, targetCollectionName) => {
		updateFilters(patch, targetCollectionName);
		fetchWith({ ...filters, ...patch });
	};
	const applyClearAndUpdateFilters = (patch, targetCollectionName) => {
		clearAndUpdateFilters(patch, targetCollectionName);
		fetchWith(patch || {});
	};

	// Debounced search that refetches on every change (including clear-to-empty).
	// Ref-wrapped so the debounced fn is stable across renders but always calls the
	// latest handler (with the current status filter).
	const applySearchRef = React.useRef<any>(null);
	applySearchRef.current = (value) =>
		applyUpdateFilters({ search: value && value.trim() ? value.trim() : undefined }, TC_NAME);
	const debouncedSearch = React.useRef(
		debounce((value) => applySearchRef.current(value), 400, false)
	);
	const onSearchChange = (event) => {
		const value = event.target.value;
		setSearchText(value);
		debouncedSearch.current(value);
	};

	const openCreate = () => {
		setEditingId(null);
		setModalVisible(true);
	};

	const openEdit = () => {
		setEditingId(nullSafeGet('id', detail));
		setModalVisible(true);
	};

	const handleSaved = (saved) => {
		loadList();
		if (saved && saved.id) {
			selectDeliverable(saved.id);
		}
	};

	const handleDelete = () => {
		runWithMessageFeedback(deleteDeliverable({ ...detail, projectId }), {
			key: MESSAGE_KEY,
			loading: 'Deleting deliverable…',
			success: 'Deliverable deleted',
			failure: 'Failed to delete deliverable',
		}).then(() => loadList().then((list) => selectFirstOrClear(list)));
	};
	const patchDeliverable = (overrides) => {
		const dueDate = nullSafeGet('dueDate', detail);
		const budget = nullSafeGet('budget', detail);
		return runWithMessageFeedback(
			updateDeliverable({
				id: nullSafeGet('id', detail),
				projectId: Number(projectId),
				name: nullSafeGet('name', detail),
				description: nullSafeGetOrElse('description', detail, ''),
				dueDate: dueDate ? moment(dueDate).toISOString() : null,
				budget: budget != null ? Number(budget) : undefined,
				assignedTo: nullSafeGetOrElse('assignedTo', detail, []),
				workOrderIds: nullSafeGetOrElse('workOrderIds', detail, []).map((id) => Number(id)),
				gallery: nullSafeGetOrElse('gallery', detail, []),
				attachments: nullSafeGetOrElse('attachments', detail, []),
				status: nullSafeGet('status', detail),
				// Full-object update: omitting this would clear the dependency every time
				// someone flipped the status from the detail pane.
				dependsOnDeliverableId: nullSafeGet('dependsOnDeliverableId', detail),
				...overrides,
			}),
			{
				key: MESSAGE_KEY,
				loading: 'Updating deliverable…',
				success: 'Deliverable updated',
				failure: 'Failed to update deliverable',
			}
		).then(() => loadList());
	};

	// detail is already keyed to the URL id via getEntityById; empty until loaded.
	const selectedDeliverable = nullSafeGet('id', detail) ? detail : null;

	// Backend list supports search + status (single). Assignee is deliberately absent:
	// ProjectDeliverable.assigned_to is an array column with no contains predicate in
	// the API yet, so the control would show an active pill over unfiltered results.
	const filterConfig = [
		{
			// RADIO_GROUP: a single-select over static `items`, which is the shape
			// SelectedFilters needs to label a pill and to clear the field from it.
			label: 'Status',
			fieldName: 'status',
			type: FILTER_FIELD_TYPE.RADIO_GROUP,
			placeholder: 'Status',
			component: StaticOptionsSelect,
			items: DELIVERABLE_STATUS_OPTIONS.map((status) => ({
				value: status,
				label: DELIVERABLE_STATUS_DISPLAY_NAMES[status],
			})),
			renderItem: (option) => option.label,
			valueAccessor: (option) => option.value,
			filtersValueAccessor: (slice) => nullSafeGet(`${TC_NAME}.filters.status`, slice),
			handleChange: (value) => applyUpdateFilters({ status: value }, TC_NAME),
		},
	];

	// A search/status filter is active — keep the list column (and its filter bar)
	// visible on zero results so the user can clear it, rather than showing the
	// full-page "no deliverables yet" state.
	const hasActiveFilters = !!(nullSafeGet('search', filters) || nullSafeGet('status', filters));
	// Full-page loader only on the initial load; once the user searches/filters we keep
	// the two-pane layout and show the loader inside the list column instead.
	const isLoading = fetching && deliverables.length === 0 && !hasActiveFilters && !searchText;
	const isEmpty = !fetching && deliverables.length === 0 && !hasActiveFilters;

	return (
		<div className="flex bg-white">
			{isLoading ? (
				<div className="flex flex-1 items-center justify-center">
					<PageLoadingPlaceholder />
				</div>
			) : isEmpty ? (
				<div className="flex flex-1 items-center justify-center p-6">
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
								alt="No deliverables"
							/>
						}
						headline="No deliverables yet"
						body={
							<div className="flex flex-col items-center gap-4">
								<div className="text-center" style={{ maxWidth: 360 }}>
									Create a deliverable to break this project into tracked units of work.
								</div>
								<Button type="primary" size="large" icon={<PlusOutlined />} onClick={openCreate}>
									New Deliverable
								</Button>
							</div>
						}
					/>
				</div>
			) : (
				<Row className="w-full" align="stretch">
					<Col span={8} className="border-0 border-r border-solid border-owgray">
						<div className="flex flex-col">
							<div className="flex items-center justify-between px-4 pt-4">
								<span className="h5medium text-character-primary">
									<FileDoneOutlined /> Deliverables ({deliverables.length})
								</span>
								<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
									New
								</Button>
							</div>
							<div className="flex items-center gap-2 px-4 py-3">
								<Input.Search
									allowClear
									placeholder="Search"
									value={searchText}
									onChange={onSearchChange}
									style={{ maxWidth: 240 }}
								/>
								<Sorter items={DELIVERABLE_SORTERS} value={sorting} onChange={onSortChange} />
								<AdvancedFilters
									filtersTargetCollectionName={TC_NAME}
									filtersStateSlice={deliverablesSlice}
									clearAndUpdateFilters={applyClearAndUpdateFilters}
									updateFilters={applyUpdateFilters}
									filterConfig={filterConfig}
									showSearch={false}
								/>
							</div>
							{/* Pills for the active status / assignee filters; renders nothing when
							    none is set, with its own spacing so no empty wrapper is left behind. */}
							<SelectedFilters
								updateFilters={(patch) => applyUpdateFilters(patch, TC_NAME)}
								filterValues={filters}
								filterConfig={filterConfig}
								pillsClosable
								containerClassName="flex flex-wrap items-center gap-2 px-4 pb-3"
							/>
							<div>
								{fetching ? (
									<div className="flex items-center justify-center py-12">
										<Spin />
									</div>
								) : deliverables.length === 0 ? (
									<div className="bodyregular px-4 py-6 text-character-secondary">
										No deliverables match your filters.
									</div>
								) : (
									deliverables.map((deliverable) => (
										<DeliverableListItem
											key={deliverable.id}
											deliverable={deliverable}
											currency={currency}
											dependsOnName={nullSafeGet(
												'name',
												getDependsOnDeliverable(deliverable, allDeliverables)
											)}
											selected={`${deliverable.id}` === `${selectedId}`}
											onClick={() => selectDeliverable(deliverable.id)}
										/>
									))
								)}
							</div>
						</div>
					</Col>
					<Col span={16}>
						<div>
							{selectedDeliverable ? (
								<DeliverableDetailPane
									key={nullSafeGet('id', selectedDeliverable)}
									deliverable={selectedDeliverable}
									currency={currency}
									dependsOnDeliverable={getDependsOnDeliverable(
										selectedDeliverable,
										allDeliverables
									)}
									dependentCount={
										getAllDependents(nullSafeGet('id', selectedDeliverable), allDeliverables).length
									}
									onSelectDeliverable={selectDeliverable}
									onEdit={openEdit}
									onDelete={handleDelete}
									onPatch={patchDeliverable}
								/>
							) : (
								<div className="flex items-center justify-center p-12">
									<EmptyState
										headline="Select a deliverable"
										body={<p>Choose a deliverable to view its details.</p>}
									/>
								</div>
							)}
						</div>
					</Col>
				</Row>
			)}

			{modalVisible ? (
				<DeliverableFormModal
					projectId={projectId}
					deliverableId={editingId}
					onClose={() => setModalVisible(false)}
					onSaved={handleSaved}
				/>
			) : null}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	deliverables: getRecordsForTargetCollection(state.deliverables, TC_NAME),
	// Unfiltered, for dependency questions — see DELIVERABLES_GRAPH_TC.
	allDeliverables: getRecordsForTargetCollection(state.deliverables, DELIVERABLES_GRAPH_TC),
	detail: getEntityById(ownProps.match.params.deliverableId, state.deliverables),
	fetching: nullSafeGetOrElse(`${TC_NAME}.fetching`, state.deliverables, false),
	filters: nullSafeGetOrElse(`${TC_NAME}.filters`, state.deliverables, {}),
	deliverablesSlice: state.deliverables,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	// sorting ({ sort_by, order }) is chosen in the component; `order` is antd-style
	// ('ascend'/'descend') which the read thunk maps to asc/desc.
	fetchDeliverables: (params, sorting) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.read(
						params,
						TC_NAME,
						{ current: 1, pageSize: 50 },
						sorting
				  )
				: deliverablesRestCrudThunksForBuyer.read(
						params,
						TC_NAME,
						{ current: 1, pageSize: 50 },
						sorting
				  )
		),
	// The whole project's deliverables, no filters, for the dependency graph. Every
	// dependency helper treats this collection as the complete graph, so one page is not
	// enough: keep going until a page comes back short, appending after the first.
	//
	// GET /project/project_deliverables does not honour no_pagination — the route lands
	// on CRUHelper.retrieveAll, which reads request.getPagination unconditionally and
	// defaults to a limit of 10 — so paging is the only way to get all of them.
	fetchAllDeliverables: (projectId) =>
		dispatch((innerDispatch, getState) => {
			const thunks =
				ownProps.userType === ROLE_TYPES.SUPPLIER
					? deliverablesRestCrudThunksForSupplier
					: deliverablesRestCrudThunksForBuyer;
			const fetchPage = (current, fetchedSoFar) =>
				innerDispatch(
					thunks.read(
						{ projectId },
						DELIVERABLES_GRAPH_TC,
						{ current, pageSize: GRAPH_PAGE_SIZE },
						null,
						null,
						current > 1
					)
				).then((rows) => {
					const fetched = fetchedSoFar + nullSafeGetOrElse('length', rows, 0);
					// Two stop conditions, no arbitrary page cap: a short page is the last one,
					// and the API's own count says when we have them all. Either alone would do
					// for a well-behaved endpoint; together they also terminate if one of them
					// misreports.
					const total = nullSafeGetOrElse(
						`${DELIVERABLES_GRAPH_TC}.count`,
						getState().deliverables,
						fetched
					);
					return nullSafeGetOrElse('length', rows, 0) === GRAPH_PAGE_SIZE && fetched < total
						? fetchPage(current + 1, fetched)
						: rows;
				});
			return fetchPage(1, 0);
		}),
	getDeliverable: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.readOne(id)
				: deliverablesRestCrudThunksForBuyer.readOne(id)
		),
	deleteDeliverable: (entity) =>
		dispatch(
			// Immediate: the delete is already confirmed via Popconfirm, so skip the
			// default 4s undo delay (which otherwise leaves the row on screen).
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.deleteImmediate(entity)
				: deliverablesRestCrudThunksForBuyer.deleteImmediate(entity)
		),
	updateDeliverable: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.update(entity)
				: deliverablesRestCrudThunksForBuyer.update(entity)
		),
	updateFilters: (updatedFilters, targetCollectionName) =>
		dispatch(DELIVERABLES_CRUD_ACTION_CREATORS.updateFilters(updatedFilters, targetCollectionName)),
	clearAndUpdateFilters: (updatedFilters, targetCollectionName) =>
		dispatch(
			DELIVERABLES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(updatedFilters, targetCollectionName)
		),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(DeliverablesTabPage))
);
